import dayjs from 'dayjs';
import { ClassTripHolder } from '../../stores/DayAvailabilityStore';
import { TeachingBlockClassTripCardsQuery } from '../../types/planung-graphql-client-defs';
import { addLeadingZero } from '../../utils/helper';

/** Eine Klassenfahrt aus einem fremden aktiven Plan, fertig für Index und Karte. */
export type ForeignClassTrip = {
  from: Date;
  to: Date;
  holders: ClassTripHolder[];
  /** Kurzname des Fachs, Aufschrift der Karte. */
  subjectShort: string;
  /** Kurznamen der Klassen, kommagetrennt. Leer, wenn keine Klasse hängt. */
  classesShort: string;
  /** Vollständiger Tooltip: Herkunft und Zeitraum. */
  tooltip: string;
};

/**
 * Reduziert die Antwort von `teachingBlockClassTripCards` auf die Fahrten, die im aktuellen
 * Board markiert werden sollen.
 *
 * Verworfen werden Karten der gerade geöffneten Version — dort steht die Fahrt bereits als Karte
 * im Board. Ungesteckte Karten liefert das Backend gar nicht erst.
 *
 * `classTripLabel` ist der bereits übersetzte Begriff („Klassenfahrt"); die Funktion bleibt
 * dadurch frei von i18n und ohne Aufbau testbar.
 */
export function collectForeignClassTrips({
  data,
  currentVersionUuid,
  classTripLabel,
}: {
  data: TeachingBlockClassTripCardsQuery | undefined;
  currentVersionUuid: string | undefined;
  classTripLabel: string;
}): ForeignClassTrip[] {
  const trips: ForeignClassTrip[] = [];

  data?.teachingBlockClassTripCards.forEach((card) => {
    // Die Fahrt der gerade geöffneten Version steht bereits als Karte im Board.
    if (card.versionUuid === currentVersionUuid) return;

    // Das Backend liefert nur gesteckte Karten und jede Klasse, Lehrkraft und jeden Raum einmal.
    const holders: ClassTripHolder[] = [
      ...card.classes.map(({ uuid }) => ({ type: 'class' as const, uuid })),
      ...card.teacherUuids.map((uuid) => ({ type: 'teacher' as const, uuid })),
      ...card.roomUuids.map((uuid) => ({ type: 'room' as const, uuid })),
    ];

    const subjectShort = card.subjectShortName || card.subjectName;
    const classesShort = card.classes
      .map((c) => c.shortName)
      .filter((name) => !!name)
      .join(', ');

    // Lokaler Tagesbeginn wie in der Karten-Zuordnung des Boards
    // (TeachingBlockContext), damit sich beide Quellen nicht um einen Tag unterscheiden.
    const from = dayjs(card.startDate).startOf('day').toDate();
    const to = dayjs(card.endDate).startOf('day').toDate();

    trips.push({
      from,
      to,
      holders,
      subjectShort,
      classesShort,
      // „Klassenfahrt · aus Epoche 1 – v01 · 12.10. – 16.10.2026" — Herkunft sagt dem
      // Planer, wo er nachschauen muss, der Zeitraum hilft bei Karten am Rand des
      // sichtbaren Bereichs.
      tooltip: [
        classTripLabel,
        `aus ${card.subjectContainerName} – v${addLeadingZero(card.version)}`,
        `${dayjs(from).format('DD.MM.')} – ${dayjs(to).format('DD.MM.YYYY')}`,
      ].join(' · '),
    });
  });

  return trips;
}
